import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { AutoSaveTextArea } from '../../utils/AutoSaveTextArea';
import { useDebounceRequest } from '../../../hooks/useDebounceRequest';
import { useRmxServiceApi } from '../api';
import { type GenericOptions, ReactSelect } from '../../utils/ReactSelect/ReactSelect';
import { useMutation } from '@tanstack/react-query';
import type { Sample } from '../Service.types';
import { useShareModalContext } from '../../hooks/ShareModalContext';
import type { RmxServiceSampleModalParam } from '../RmxServiceSampleModal/RmxServiceSampleModal';
import { reportException } from '../../../util/sentryProvider';

interface Props {
  sample: Sample;
  refetchSampleHistory?: () => Promise<unknown>;
  disabled?: boolean;
}

export function RmxServiceSampleReview({ sample, refetchSampleHistory, disabled }: Props) {
  const [reviewText, setReviewText] = React.useState<string>(sample?.reviewDescription ?? '');
  const [reviewOutcome, setReviewOutcome] = useState<string>(sample?.reviewOutcome ?? 'Select');
  const modalState = useShareModalContext<RmxServiceSampleModalParam, unknown>();

  const reviewOutcomeOptions = [
    { value: 'Ignore', label: 'Ignore' },
    { value: 'Resample', label: 'Resample' },
    { value: 'Change', label: 'Change' }
  ] satisfies GenericOptions<string>[]; // (

  const api = useRmxServiceApi();

  const { handleDebounceRequest: updateDescriptionAndAsset, statusInfo: reviewTextAutoSaveStatus } = useDebounceRequest({
    fn: async (s: { sampleId: number; description: string | null; outcome: string | null }, signal) => {
      await api.updateSampleReviewDescriptionAndOutcome(s, signal);
      await refetchSampleHistory?.();
    },
    debounceTime: 1000
  });

  const {
    mutateAsync: submitSampleReview,
    isPending: executingSubmit,
    isError
  } = useMutation({
    mutationFn: async (s: { sampleId: number; description: string; outcome: string }) => {
      await api.submitSampleReview(s);
      modalState.close();
    },
    onError: (e) => {
      reportException(e);
    }
  });

  async function handleSubmit(params: { sampleId: number; description: string; outcome: string }) {
    if (params.description === null && params.outcome === null) {
      alert('Please enter a review description and select a review outcome');
    }
    await submitSampleReview(params);
    await refetchSampleHistory?.();
  }

  return (
    <div style={{ minHeight: '415px', overflowX: 'hidden', overflowY: 'hidden' }}>
      <Form noValidate={true}>
        <Row>
          <Col>
            <AutoSaveTextArea
              label={'Review'}
              readonly={false}
              disabled={executingSubmit || sample.reviewed || disabled}
              setValue={async (e) => {
                setReviewText(e);
                await updateDescriptionAndAsset({ sampleId: sample.id, description: e, outcome: null });
              }}
              statusInfo={reviewTextAutoSaveStatus}
              value={reviewText}
            />
            <div className={'mb-3'}>
              <Form.Label>Review Outcome</Form.Label>
              <ReactSelect
                disabled={executingSubmit || sample.reviewed || disabled}
                defaultValue={reviewOutcome}
                value={reviewOutcome}
                options={reviewOutcomeOptions}
                onChange={async (e) => {
                  setReviewOutcome(e);
                  await updateDescriptionAndAsset({ sampleId: sample.id, description: null, outcome: e });
                }}
              />
            </div>
          </Col>
        </Row>
        <Row className={'mb-3'}>
          <Col>
            <Button
              disabled={executingSubmit || sample.reviewed || disabled}
              onClick={async () => {
                await handleSubmit({ sampleId: sample.id, description: reviewText, outcome: reviewOutcome });
                //
              }}
              variant='secondary'
              style={{ width: '100%', display: 'block' }}
            >
              Submit
            </Button>
          </Col>
        </Row>
        {isError && <Alert variant='danger'>Submission failed. Support has been notified.</Alert>}{' '}
      </Form>
    </div>
  );
}
